import React, { useMemo, useState } from "react";

// A dependency-free emoji picker: a searchable, categorized grid. Emojis are inserted as plain
// Unicode characters (they round-trip through Markdown as text), so no editor extension is
// needed. The set is curated to the symbols issue descriptions actually use rather than the full
// Unicode table — keeps the bundle tiny and the grid scannable.

interface EmojiEntry {
  e: string;
  k: string; // space-separated search keywords
}

interface EmojiGroup {
  name: string;
  items: EmojiEntry[];
}

const EMOJI_GROUPS: EmojiGroup[] = [
  {
    name: "Smileys",
    items: [
      { e: "😀", k: "grin happy smile" },
      { e: "😃", k: "smile happy" },
      { e: "😄", k: "laugh happy" },
      { e: "😁", k: "grin beam" },
      { e: "😆", k: "laugh xd" },
      { e: "😅", k: "sweat laugh nervous" },
      { e: "😂", k: "joy laugh cry tears" },
      { e: "🤣", k: "rofl rolling laugh" },
      { e: "🙂", k: "slight smile" },
      { e: "🙃", k: "upside down silly" },
      { e: "😉", k: "wink" },
      { e: "😊", k: "blush smile happy" },
      { e: "😍", k: "heart eyes love" },
      { e: "🥰", k: "love hearts" },
      { e: "😎", k: "cool sunglasses" },
      { e: "🤩", k: "star struck wow" },
      { e: "🤔", k: "thinking hmm" },
      { e: "🤨", k: "raised eyebrow skeptical" },
      { e: "😐", k: "neutral meh" },
      { e: "😴", k: "sleep tired zzz" },
      { e: "😭", k: "sob cry" },
      { e: "😡", k: "angry mad rage" },
      { e: "🥳", k: "party celebrate" },
      { e: "😱", k: "scream shock" },
      { e: "🤯", k: "mind blown exploding" },
      { e: "😬", k: "grimace awkward" },
      { e: "🙄", k: "eye roll" },
      { e: "😏", k: "smirk" },
      { e: "🤓", k: "nerd glasses" },
      { e: "🥵", k: "hot sweat" }
    ]
  },
  {
    name: "Gestures",
    items: [
      { e: "👍", k: "thumbs up like approve yes" },
      { e: "👎", k: "thumbs down dislike no" },
      { e: "👌", k: "ok perfect" },
      { e: "🙏", k: "pray thanks please" },
      { e: "👏", k: "clap applause" },
      { e: "🙌", k: "raise hands celebrate" },
      { e: "💪", k: "muscle strong flex" },
      { e: "✌️", k: "peace victory" },
      { e: "🤞", k: "fingers crossed hope" },
      { e: "🤝", k: "handshake deal agree" },
      { e: "👋", k: "wave hi hello bye" },
      { e: "✍️", k: "writing note" },
      { e: "👀", k: "eyes look watching" },
      { e: "🫶", k: "heart hands love" }
    ]
  },
  {
    name: "Symbols",
    items: [
      { e: "✅", k: "check done complete yes tick" },
      { e: "❌", k: "cross no wrong fail" },
      { e: "⚠️", k: "warning caution alert" },
      { e: "❓", k: "question" },
      { e: "❗", k: "exclamation important" },
      { e: "💯", k: "hundred perfect score" },
      { e: "🔥", k: "fire hot lit" },
      { e: "⭐", k: "star favorite" },
      { e: "✨", k: "sparkles shiny new" },
      { e: "⚡", k: "zap lightning fast" },
      { e: "💥", k: "boom collision" },
      { e: "❤️", k: "heart love red" },
      { e: "💔", k: "broken heart" },
      { e: "✔️", k: "check mark done" },
      { e: "➕", k: "plus add" },
      { e: "➖", k: "minus remove" },
      { e: "🚫", k: "no forbidden block" },
      { e: "🔒", k: "lock secure" },
      { e: "🔓", k: "unlock open" },
      { e: "🔑", k: "key access" }
    ]
  },
  {
    name: "Work",
    items: [
      { e: "🚀", k: "rocket launch ship release" },
      { e: "🐛", k: "bug defect issue" },
      { e: "🔧", k: "wrench fix tool" },
      { e: "🛠️", k: "tools build" },
      { e: "⚙️", k: "gear settings config" },
      { e: "💡", k: "idea bulb tip" },
      { e: "📝", k: "memo note write" },
      { e: "📌", k: "pin important" },
      { e: "📅", k: "calendar date schedule" },
      { e: "⏰", k: "alarm time deadline" },
      { e: "⏳", k: "hourglass waiting pending" },
      { e: "🎯", k: "target goal aim" },
      { e: "🏆", k: "trophy win award" },
      { e: "🎉", k: "party tada celebrate launch" },
      { e: "📈", k: "chart up growth" },
      { e: "📉", k: "chart down decline" },
      { e: "💰", k: "money cost budget" },
      { e: "🔍", k: "search find magnify" },
      { e: "💻", k: "laptop code dev" },
      { e: "📱", k: "phone mobile" },
      { e: "🧪", k: "test experiment lab" },
      { e: "🔬", k: "microscope research" },
      { e: "📦", k: "package box ship release" },
      { e: "🗑️", k: "trash delete remove" }
    ]
  }
];

interface EmojiPickerProps {
  onPick: (emoji: string) => void;
}

const EmojiPicker: React.FC<EmojiPickerProps> = ({ onPick }) => {
  const [query, setQuery] = useState<string>("");

  const groups: EmojiGroup[] = useMemo(() => {
    const q: string = query.trim().toLowerCase();
    if (!q) return EMOJI_GROUPS;
    return EMOJI_GROUPS.map((group: EmojiGroup) => ({
      name: group.name,
      items: group.items.filter((entry: EmojiEntry) => entry.k.includes(q))
    })).filter((group: EmojiGroup) => group.items.length > 0);
  }, [query]);

  return (
    // Keep the editor's selection/focus while interacting with the picker.
    <div className="kuika-emoji-picker" onMouseDown={(event: React.MouseEvent) => event.preventDefault()}>
      <input
        className="kuika-emoji-picker__search"
        value={query}
        placeholder="Search emoji…"
        autoFocus
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
      />
      <div className="kuika-emoji-picker__scroll">
        {groups.length === 0 && <div className="kuika-emoji-picker__empty">No emoji</div>}
        {groups.map((group: EmojiGroup) => (
          <div key={group.name} className="kuika-emoji-picker__group">
            <div className="kuika-emoji-picker__group-name">{group.name}</div>
            <div className="kuika-emoji-picker__grid">
              {group.items.map((entry: EmojiEntry) => (
                <button
                  key={entry.e}
                  type="button"
                  className="kuika-emoji-picker__emoji"
                  title={entry.k.split(" ")[0]}
                  onClick={() => onPick(entry.e)}
                >
                  {entry.e}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EmojiPicker;
